// Canonical Sprint Initialization. One T0. Every gate date is derived from it.
// Changing T0 after the sprint begins is a Re-baseline Decision, not a setting.
// Bridge45 locks operational dates to the 45-day window [T0, Day45].

import { SPRINT } from './constants';
import { addDays, isAfter, isBefore, parseISO } from './dates';

export const CANONICAL_SPRINT_START = '2026-08-17';

export function sprintInitialization(partial = {}) {
  return {
    start: String(partial.start || CANONICAL_SPRINT_START).slice(0, 10),
    initializedAt: String(partial.initializedAt || partial.start || CANONICAL_SPRINT_START).slice(0, 10),
    initializedBy: partial.initializedBy || 'system:seed',
    kind: partial.kind || 'initialization',
    lastRebaselineDecisionId: partial.lastRebaselineDecisionId || '',
    lastRebaselineAt: partial.lastRebaselineAt || '',
    lastRebaselineBy: partial.lastRebaselineBy || '',
    lastRebaselineReason: partial.lastRebaselineReason || '',
  };
}

export function sprintStartOf(state) {
  const init = state?.settings?.sprint;
  if (init?.start) return String(init.start).slice(0, 10);
  if (state?.settings?.sprintStart) return String(state.settings.sprintStart).slice(0, 10);
  return CANONICAL_SPRINT_START;
}

/** Dual-write both T0 fields so legacy and canonical stay aligned. */
export function dualSprintSettings(start, partial = {}) {
  const nextStart = String(start).slice(0, 10);
  return {
    sprintStart: nextStart,
    sprint: {
      ...sprintInitialization({ ...partial, start: nextStart }),
      start: nextStart,
    },
  };
}

/**
 * Hard 45-day lock: Day 1 = T0, Day 45 = T0 + 44.
 */
export function sprintWindow(stateOrStart) {
  const start =
    typeof stateOrStart === 'string'
      ? String(stateOrStart).slice(0, 10)
      : sprintStartOf(stateOrStart);
  const end = addDays(start, SPRINT.totalDays - 1);
  return { start, end, totalDays: SPRINT.totalDays };
}

/** Day index 1..45 when date is inside the window; null otherwise. */
export function dayIndexFromT0(date, t0) {
  const d = String(date || '').slice(0, 10);
  const start = String(t0 || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
  const { end } = sprintWindow(start);
  if (isBefore(d, start) || isAfter(d, end)) return null;
  // diffDays(start, d) + 1
  const ms = parseISO(d) - parseISO(start);
  const days = Math.round(ms / 86400000);
  return days + 1;
}

export function isWithinSprintWindow(date, stateOrStart) {
  const d = String(date || '').slice(0, 10);
  if (!d) return false;
  const { start, end } = sprintWindow(stateOrStart);
  return !isBefore(d, start) && !isAfter(d, end);
}

/**
 * Validate an operational date. Returns null if OK, else an error string.
 * Empty dates are allowed (optional fields).
 */
export function assertWithinSprintWindow(date, stateOrStart, field = 'date') {
  const d = String(date || '').trim().slice(0, 10);
  if (!d) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return `${field} must be an ISO date (YYYY-MM-DD)`;
  const { start, end } = sprintWindow(stateOrStart);
  if (isBefore(d, start) || isAfter(d, end)) {
    return `${field} ${d} is outside the 45-day sprint window [${start} → ${end}]`;
  }
  return null;
}

/** Default due for new founder moves: T0 in pre-sprint, else wall-clock today. */
export function defaultOperationalDue(sprint) {
  if (!sprint) return '';
  if (sprint.phase === 'pre' || !sprint.executionAsOf) return sprint.start || '';
  return sprint.today || '';
}

/** Overdue / aging / time-pressure only after execution begins. */
export function canEvaluateTimePressure(sprint) {
  return !!(sprint && sprint.executionAsOf);
}

/**
 * Integrity: persisted initialization and the derived clock must agree.
 * A mismatch is a STATE INTEGRITY failure, not a second calendar.
 */
export function sprintIntegrity(state) {
  const issues = [];
  const init = state?.settings?.sprint;
  const start = sprintStartOf(state);
  if (!init || !init.start) {
    issues.push('Sprint Initialization is missing. All dates must derive from one T0.');
  } else {
    if (state?.settings?.sprintStart && String(state.settings.sprintStart).slice(0, 10) !== start) {
      issues.push(`settings.sprintStart (${state.settings.sprintStart}) disagrees with Sprint Initialization (${init.start}).`);
    }
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) {
    issues.push('Sprint start is not an ISO date.');
  }
  const rebaselined = init?.kind === 'rebaseline' || !!init?.lastRebaselineDecisionId;
  return {
    ok: issues.length === 0,
    issues,
    start,
    rebaselined,
    rebaselineLabel: rebaselined && init?.lastRebaselineAt
      ? `Sprint re-baselined on ${init.lastRebaselineAt}${init.lastRebaselineDecisionId ? ` by Decision ${init.lastRebaselineDecisionId}` : ''}`
      : '',
  };
}

export function applyRebaseline(state, { start, reason, decisionId, by, today }) {
  const nextStart = String(start).slice(0, 10);
  return dualSprintSettings(nextStart, {
    ...(state.settings?.sprint || {}),
    kind: 'rebaseline',
    lastRebaselineDecisionId: decisionId || '',
    lastRebaselineAt: today,
    lastRebaselineBy: by || 'Founder',
    lastRebaselineReason: reason || '',
  });
}
